/**
 * components/PrereqBanner.jsx
 * Collapsible pre-requisite info banner shown on relevant pages.
 * No blocking — informational only.
 */
import { useState } from "react";

const PREREQS = {
  oci: {
    title: "OCI API access required for auto-pull",
    steps: [
      {
        label: "Create billing reader user + group",
        detail: "Identity & Security → Users → Create User: beyondata-billing-reader\nIdentity & Security → Groups → Create: beyondata-billing-readers → Add user"
      },
      {
        label: "Create IAM policy at ROOT compartment",
        detail: "Identity & Security → Policies → Create Policy (at root tenancy level)\n\nAdd these 3 statements:\n  Allow group beyondata-billing-readers to read usage-report in tenancy\n  Allow group beyondata-billing-readers to inspect usage-report in tenancy\n  Allow group beyondata-billing-readers to read cost-reports in tenancy"
      },
      {
        label: "Generate API key and share with platform admin",
        detail: "Users → beyondata-billing-reader → API Keys → Add API Key\n→ Generate API Key Pair\n→ Download private key (.pem) — only downloadable once\n→ Copy config snippet (user OCID, fingerprint, tenancy OCID, region)\n→ Share both with platform admin to enter in Client Settings → OCI Credentials"
      },
    ],
    note: "CSV upload works without OCI API access. Only auto-pull requires these steps."
  },
  alerts: {
    title: "OCI Notifications setup required for email alerts",
    steps: [
      {
        label: "Create a Notifications topic",
        detail: "OCI Console → Application Integration → Notifications → Topics → Create Topic\nName: beyondata-cost-alerts"
      },
      {
        label: "Add email subscription and confirm",
        detail: "Click on the topic → Subscriptions → Create Subscription\nProtocol: Email → enter recipient email\n→ Check inbox and click CONFIRM SUBSCRIPTION link\n→ Status must show Active (not Pending)"
      },
      {
        label: "Add notifications policy at ROOT",
        detail: "Identity & Security → Policies → Create Policy (at root tenancy level)\n\nStatement:\n  Allow any-user to use ons-topic in tenancy\n  where request.permission='ONS_TOPIC_PUBLISH'\n\n→ Copy Topic OCID and enter in Client Settings → Alerts & Notifications"
      },
    ],
    note: "Alerts will not fire until the topic OCID is configured in Client Settings → Alerts & Notifications."
  },
  compartments: {
    title: "OCI credentials required for compartment analysis",
    steps: [
      {
        label: "Complete OCI API access setup",
        detail: "See Data Ingestion page for full steps to create billing reader user, group, IAM policy and API key."
      },
      {
        label: "Configure credentials in Client Settings",
        detail: "Go to Client Settings → OCI Credentials\nEnter: Profile name, Tenancy OCID, User OCID, Fingerprint, Region, Private key\n→ Click Save then Test Connection"
      },
      {
        label: "Pull data for at least one month",
        detail: "Go to Data Ingestion → select month → Auto-pull from OCI API\nCompartment analysis is populated from the pulled data."
      },
    ],
    note: "Compartment analysis shows cost breakdown by OCI compartment — requires at least one month of API-pulled data."
  },
};

export default function PrereqBanner({ type }) {
  const [open, setOpen]         = useState(false);
  const [expanded, setExpanded] = useState({});
  const prereq = PREREQS[type];
  if (!prereq) return null;

  const toggle = (i) => setExpanded(p => ({ ...p, [i]: !p[i] }));

  return (
    <div style={{
      border: "1px solid #FDE68A",
      borderRadius: 8,
      marginBottom: 20,
      overflow: "hidden",
      background: "#FFFBEB",
    }}>
      {/* Header — always visible */}
      <div
        onClick={() => setOpen(p => !p)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px", cursor: "pointer",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15 }}>ℹ</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#92400E" }}>
            Pre-requisite: {prereq.title}
          </span>
        </div>
        <span style={{ fontSize: 12, color: "#92400E", fontWeight: 600 }}>
          {open ? "Hide ▲" : "Show steps ▼"}
        </span>
      </div>

      {/* Expandable body */}
      {open && (
        <div style={{ borderTop: "1px solid #FDE68A", padding: "12px 14px", background: "#fff" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            {prereq.steps.map((step, i) => (
              <div key={i} style={{
                border: "0.5px solid #E5E7EB",
                borderRadius: 6,
                overflow: "hidden",
              }}>
                <div
                  onClick={() => toggle(i)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 12px", cursor: "pointer",
                    background: expanded[i] ? "#F8FAFC" : "#fff",
                  }}
                >
                  <span style={{
                    width: 20, height: 20, borderRadius: "50%",
                    background: "#EFF6FF", color: "#2563EB",
                    fontSize: 11, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>{i + 1}</span>
                  <span style={{ fontSize: 13, color: "#374151", flex: 1 }}>{step.label}</span>
                  <span style={{ fontSize: 11, color: "#9CA3AF" }}>{expanded[i] ? "▲" : "▼"}</span>
                </div>
                {expanded[i] && (
                  <div style={{
                    padding: "10px 12px",
                    borderTop: "0.5px solid #F3F4F6",
                    fontFamily: "monospace", fontSize: 12,
                    color: "#374151", lineHeight: 1.7,
                    background: "#F8FAFC",
                    whiteSpace: "pre-wrap",
                  }}>
                    {step.detail}
                  </div>
                )}
              </div>
            ))}
          </div>
          {prereq.note && (
            <div style={{
              background: "#EFF6FF", border: "1px solid #BFDBFE",
              borderRadius: 6, padding: "8px 12px",
              fontSize: 12, color: "#1E40AF",
            }}>
              ℹ {prereq.note}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
